<template>
  <div class="content">
    <div class="header d-flex justify-content-between align-items-center mb-4">
      <div class="title">
        <h3 class="h3 mb-n2 font-weight-bold">Lotes</h3>
        <span class="subtitle-2 grey--text text--darken-1">Gerenciamento de Pareceres Técnicos</span>
      </div>
      <div class="actions d-flex flex-row align-center">
        <v-btn class="elevation-4 mr-2" @click="refreshAll" outlined>
          <v-icon left dark>mdi-refresh</v-icon>
          <span style="font-size: 12px">Atualizar</span>
        </v-btn>
      </div>
    </div>

    <v-row align="center" class="mt-2 mb-4 mx-0">
      <v-col cols="12" sm="3" md="2">
        <year-select 
          v-model="filters.anoBase" 
          @change="refreshAll"
          outlined 
          dense 
          hide-details
        />
      </v-col>
      <v-col cols="12" sm="4" md="3">
        <v-select
          v-model="filters.tipo"
          :items="opts.loteTypes"
          item-text="name"
          item-value="id"
          placeholder="Tipo de parecer"
          outlined
          dense
          hide-details
          clearable
          @change="refreshAll"
        ></v-select>
      </v-col>
      <v-col cols="12" sm="5" md="4">
        <v-text-field
          v-model="search"
          append-icon="mdi-magnify"
          placeholder="Buscar no histórico..."
          hide-details
          outlined
          dense
        />
      </v-col>
      <v-spacer />
      <v-col cols="auto" v-if="canAdd && !clientId">
        <v-btn 
          class="elevation-4" 
          color="primary" 
          style="background-color: #1c1c44;" 
          @click="openAddModal"
        >
          <v-icon left>mdi-plus</v-icon>
          <span style="font-size: 12px">Adicionar</span>
        </v-btn>
      </v-col>
    </v-row>

    <div class="body">
      <!-- Empty state when a company is selected -->
      <div v-if="clientId" class="text-center mt-10">
        <v-icon size="64" color="warning">mdi-alert-circle-outline</v-icon>
        <h4 class="mt-4 grey--text text--darken-1">Lotes Globais</h4>
        <p class="grey--text">Os Lotes são gerenciados de forma global. Desselecione a empresa no topo para visualizar os dados.</p>
      </div>

      <div v-else class="teros-elevation rounded mb-6">
        <v-data-table
          class="mb-6"
          :headers="computedHeaders"
          :items="items"
          :options.sync="options"
          :loading="loading"
          :search="search"
          :footer-props="{
            'items-per-page-options': [10, 15, 30, 50, -1],
            'items-per-page-text': 'Itens por página'
          }"
        >
          <template v-slot:item.tipo_label="{ item }">
            <v-chip
              small
              :color="getTipoColor(item.tipo)"
              dark
            >
              {{ item.tipo_label }}
            </v-chip>
          </template>

          <template v-slot:item.anexo="{ item }">
            <v-btn
              v-if="item.anexo"
              icon
              small
              color="primary"
              :href="item.anexo"
              target="_blank"
              title="Baixar Arquivo"
            >
              <v-icon>mdi-download</v-icon>
            </v-btn>
          </template>

          <template v-slot:item.acoes="{ item }">
            <v-btn v-if="canDelete" icon small color="error" @click="confirmDelete(item)" title="Remover">
              <v-icon>mdi-delete</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </div>
    </div>

    <!-- Single Modal for all types -->
    <FormModal
      :max-width="600"
      :title="'Adicionar Lote'"
      :opened.sync="isVisibleFormModal"
      :value.sync="formModalData"
      :errorMessage="errorMessage"
      :cols="colsForm"
      :opts="opts"
      cancel-button-color="white"
      @save="doSave"
    />
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import yearsOptions from "@/helpers/yearsOptions";
import { userHasAccess } from "@/helpers/permissionValidator";

export default {
  name: "LotesListing",
  components: {
    FormModal: () => import("@/components/form-modal.vue"),
    YearSelect: () => import("@/components/year-select.vue")
  },
  data() {
    return {
      filters: {
        anoBase: new Date().getFullYear().toString(),
        tipo: null
      },
      isVisibleFormModal: false,
      search: "",
      items: [],
      totalItems: 0,
      loading: false,
      options: { page: 1, itemsPerPage: 15 },
      formModalData: {},
      errorMessage: "",
      opts: {
        years: yearsOptions.filter(y => parseInt(y.value) <= new Date().getFullYear()),
        loteTypes: [
          { id: 1, name: "Parecer Técnico" },
          { id: 2, name: "Contestação" },
          { id: 3, name: "Recurso Administrativo" }
        ]
      },
      cols: [
        { value: "id", text: "ID", align: "start", sortable: false, width: "15px" },
        { value: "ano_base", text: "Ano-base", align: "center", sortable: false, width: "100px" },
        { value: "tipo_label", text: "Tipo de Parecer", align: "start", sortable: false },
        { value: "titulo", text: "Título/Arquivo", align: "start", sortable: false},
        { value: "anexo", text: "Baixar", align: "center", sortable: false, width: "100px" },
        { value: "acoes", text: "Ações", align: "center", sortable: false, width: "100px" }
      ],
      colsForm: [
        {
          key: "numero_lote",
          name: "Nº Lote",
          type: this.$fieldTypes.NUMBER,
          rules: [{ rule: "required" }],
          colSize: 3
        },
        {
          key: "tipo",
          name: "Tipo de Lote",
          type: this.$fieldTypes.SELECT,
          rel: { to: "loteTypes", key: "id", name: "name" },
          rules: [{ rule: "required" }],
          colSize: 9
        },        
        {
          key: "ano_base",
          name: "Ano Base",
          type: this.$fieldTypes.SELECT,
          rel: { to: "years", key: "value", name: "text" },
          rules: [{ rule: "required" }],
          colSize: 12
        },
        {
          key: "arquivo",
          name: "Arquivo (.pdf)",
          type: this.$fieldTypes.FILE2,
          rules: [{ rule: "required" }],
          colSize: 12
        }
      ]
    };
  },
  computed: {
    ...mapGetters(["user", "clientId"]),
    canAdd() {
      return userHasAccess(this.user, 'Cadastro.lotes.create');
    },
    canDelete() {
      return userHasAccess(this.user, 'Cadastro.lotes.delete');
    },
    computedHeaders() {
      let headers = this.cols;
      if (!this.canDelete) {
        headers = headers.filter(h => h.value !== 'acoes');
      }
      return headers.map(h => ({
        ...h,
        class: `vuetify-table-header${
          this.getClient && this.getClient().isClient ? "-cli" : ""
        }`
      }));
    }
  },
  mounted() {
    this.refreshAll();
  },
  watch: {
    clientId() {
      this.refreshAll();
    }
  },
  methods: {
    getTipoColor(tipoId) {
      switch (tipoId) {
        case 1: return 'info'; // Parecer Técnico
        case 2: return 'warning'; // Contestação
        case 3: return 'error'; // Recurso Administrativo
        default: return 'primary';
      }
    },
    refreshAll() {
      this.fetchData();
    },
    fetchData() {
      if (this.clientId) {
        this.items = [];
        this.totalItems = 0;
        this.$notify({
          group: "geral",
          type: "warn",
          title: "Aviso",
          text: "Os Lotes são globais. Desselecione a empresa no topo para visualizar os dados."
        });
        return;
      }

      this.loading = true;
      const resource = this.apiResource("v1/cadastro/lotes");
      const queryParams = {
        'filters[ano_base]': this.filters.anoBase,
        per_page: 999,
        empresaId: this.clientId
      };

      if (this.filters.tipo) {
        queryParams['filters[tipo]'] = this.filters.tipo;
      }

      const qs = new URLSearchParams(queryParams).toString();
      
      resource.get({ query: qs }).then(res => {
        this.items = res.data || [];
        this.totalItems = res.total || 0;
      }).catch(() => {
        this.items = [];
        this.totalItems = 0;
      }).finally(() => {
        this.loading = false;
      });
    },
    openAddModal() {
      this.formModalData = {
        ano_base: this.filters.anoBase ? parseInt(this.filters.anoBase, 10) : null,
        tipo: this.filters.tipo,
        numero_lote: null,
        arquivo: []
      };
      this.errorMessage = "";
      this.isVisibleFormModal = true;
    },
    editItem(item) {
      this.errorMessage = "";
      this.formModalData = { 
        ...item,
        ano_base: item.ano_base ? parseInt(item.ano_base, 10) : null
      };
      if(item.anexo && !Array.isArray(item.anexo)) {
        this.formModalData.arquivo = [];
      }
      this.isVisibleFormModal = true;
    },
    doSave(data) {
      if (!data.arquivo || !data.arquivo.length) {
        this.errorMessage = "O arquivo é obrigatório.";
        return;
      }

      const resource = this.apiResource("v1/cadastro/lotes");
      const formData = new FormData();
      
      formData.append("tipo", data.tipo);
      formData.append("numero_lote", data.numero_lote);
      formData.append("ano_base", data.ano_base);
      formData.append("anexo", data.arquivo[0]);
      formData.append("empresaId", this.clientId);

      resource.save(formData).then(() => {
        this.$notify({
          group: "geral",
          type: "success",
          title: "Sucesso",
          text: "Lote salvo com sucesso!"
        });
        
        this.isVisibleFormModal = false;
        this.fetchData();
      }).catch(err => {
        this.errorMessage = err.response?.data?.error
          || Object.values(err.response?.data?.errors || {}).flat()[0]
          || err.response?.data?.message
          || "Erro ao salvar lote.";
      });
    },
    confirmDelete(row) {
      this.showConfirm(
        "Confirmação",
        "Deseja realmente remover este lote?",
        () => {
          const resource = this.apiResource("v1/cadastro/lotes");
          resource.delete(`${row.id}?empresaId=${this.clientId}`).then(() => {
            this.$notify({
              group: "geral",
              type: "success",
              title: "Removido",
              text: "Lote removido com sucesso!"
            });
            this.fetchData();
          });
        }
      );
    }
  }
};
</script>

<style scoped>
.content {
  padding: 1.5rem;
}
</style>
